import React from 'react';
import { Message } from '@getro/rombo';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { Link } from 'rebass/styled-components';
import styled from 'styled-components';

import { useRelationshipStrengthSync } from '../../../hooks/useRelationshipStrengthSync';
import { activeNetworkSelector } from '../../../redux/selectors';

const Banner = styled.div`
  width: 100%;
`;

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const googleSource = ({ emailSyncEnabled, calendarSyncEnabled }) => {
  if (emailSyncEnabled === true && calendarSyncEnabled === false) {
    return { title: 'mail', presentVerb: 'is' };
  }
  if (emailSyncEnabled === false && calendarSyncEnabled === true) {
    return { title: 'calendar', presentVerb: 'is' };
  }
  return { title: 'mail and calendar', presentVerb: 'are' };
};

const bannerPresentations = (googleSync) => {
  const source = googleSource(googleSync);
  const contactAction = googleSync.contactsSyncEnabled === true ? 'adding people you interact with and ' : '';

  return {
    pending: {
      title: `Google ${source.title} ${source.presentVerb} syncing`,
      body:
        `Getro is ${contactAction}updating your connections and Reachability. ` +
        'Results may be incomplete until this finishes. You can keep using Getro.',
      type: 'info',
    },
    calculating: {
      title: 'Reachability is updating',
      body:
        'Google sync is complete. Getro is recalculating connections and Reachability scores. ' +
        'Results may be incomplete until this finishes.',
      type: 'info',
    },
    failed: {
      title: "Reachability couldn't finish updating",
      body: `Connections and Reachability may be incomplete. Review your Google ${source.title} integration to retry.`,
      type: 'warning',
    },
  };
};

export const RelationshipStrengthCalculationBanner = () => {
  const { googleSync } = useRelationshipStrengthSync();
  const activeNetwork = useSelector(activeNetworkSelector);
  const presentation = bannerPresentations(googleSync)[googleSync.relationshipStrengthSyncStatus];
  const shouldShow =
    googleSync.relationshipStrengthEnabled === true &&
    googleSync.relationshipStrengthInitialSync === true &&
    Boolean(presentation);

  if (!shouldShow) return null;

  return (
    <Banner data-testid="relationship-strength-calculation-banner">
      <Message type={presentation.type}>
        <Copy>
          <strong>{presentation.title}</strong>
          <span>{presentation.body}</span>
          {googleSync.relationshipStrengthSyncStatus === 'failed' && activeNetwork?.slug && (
            <Link
              as={NavLink}
              color="text.main"
              to={`/networks/${activeNetwork.slug}/settings/integrations`}
              sx={{ textDecoration: 'underline' }}
            >
              Review integration
            </Link>
          )}
        </Copy>
      </Message>
    </Banner>
  );
};

export default RelationshipStrengthCalculationBanner;
